import { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

//Routes
import { paths } from '../../routing/routes';

// Styles
import S from '../../styled';

// Model Requests
import { getContentReviews, getFileRequestTask } from '../../data/requests/task';
import { getUploadsByParentTaskId } from '../../data/requests/uploads';

// Components
import Page from '../../components/Page';
import Module from '../../components/Module';
import TaskCard from '../../components/TaskCard';
import DownloadFileLinks from '../../components/DownloadFileLinks';
import ModuleHeader from '../../components/ModuleHeader';
import TaskInstructions from '../../components/TaskInstructions';

// Helpers
import navigateTo from '../../helpers/navigateTo';
import { taskNameWithDueDateObject } from '../../helpers/taskNameDueDate';

export default function FileRequestTask(props) {
  const history = useHistory();

  const [fileRequestTask, setFileRequestTask] = useState({});
  const [contentReviews, setContentReviews] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [isLoadingFileRequest, setIsLoadingFileRequest] = useState(true);
  const [isLoadingUploads, setIsLoadingUploads] = useState(true);

  const { store } = props;
  const { taskId: fileRequestTaskIdQueryParam } = props.match.params;

  const { token } = store;

  const sortContentReviews = (contentReviewsToBeSorted) => {
    if (!contentReviewsToBeSorted) return null;
    return contentReviewsToBeSorted.sort((a, b) => (a.id <= b.id) ? -1 : 1);
  }

  const getContentReviewsData = useCallback(async () => {
    if (!token) return;

    setIsLoadingFileRequest(true);
    try {
      const contentReviewsToBeSorted = await getContentReviews(token, fileRequestTaskIdQueryParam);
      const sortedContentReviews = sortContentReviews(contentReviewsToBeSorted);
      setContentReviews(sortedContentReviews);

      const task = await getFileRequestTask(token, fileRequestTaskIdQueryParam);
      setFileRequestTask(task);
    } catch (error) {
      console.error('getContentReviews: ', error);
    } finally {
      setIsLoadingFileRequest(false);
    }
  }, [token, fileRequestTaskIdQueryParam]);

  const getUploads = useCallback(async () => {
    const parentTaskId = fileRequestTask.parent_task_id;
    if (!token || !parentTaskId) return;

    setIsLoadingUploads(true);
    try {
      const lookupUploads = await getUploadsByParentTaskId(token, parentTaskId);
      setUploads(lookupUploads);
    } catch (error) {
      console.error('getUploadsByTaskId', error);
    } finally {
      setIsLoadingUploads(false);
    }
  }, [token, fileRequestTask]);

  useEffect(() => {
    getContentReviewsData();
  }, [getContentReviewsData]);

  useEffect(() => {
    getUploads();
  }, [getUploads]);

  const processTaskClick = (review) => {
    navigateTo(
      history,
      paths.contentStatus,
      token,
      { 
        queryParams: {
          taskId: fileRequestTaskIdQueryParam, 
          reviewId: review.id 
        },
        pathKeyisFullPath: true
      }
    );
  }

  const moduleTitle = (
    <ModuleHeader moduleName={taskNameWithDueDateObject(fileRequestTask)} history={history} />
  );

  const getReview = (index) => {
    if (!contentReviews) return;
    if (contentReviews.length > index) return contentReviews[index];
  }

  const taskCards = (
    // NOTE: Some require review and others don't so we need the quantity. Otherwise
    // we risk rendering nothing in the case of no ContentReviews
    fileRequestTask?.quantity && Array.apply(
      null,
      { length: fileRequestTask.quantity }
    ).map((_, index) => {
      const review = getReview(index);

      return (
        <TaskCard
          key={`Task-${index}`}
          index={index}
          task={fileRequestTask}
          review={review}
          processTaskClick={processTaskClick}
          store={store}
        />
      )
    })
  );

  const contentReviewCards = (
    <S.Flex column alignCenter flex={1}>
      <S.Flex
        column
        tabletStyles={{ width: '80%' }}
        desktopStyles={{ width: '80%' }}
        paddingTop={40}
      >
        {taskCards}
      </S.Flex>
    </S.Flex>
  );

  const isLoading = isLoadingFileRequest || isLoadingUploads

  return (
    <Page>
      <Module
        flex={1}
        titleBlock={moduleTitle}
        isLoading={isLoading}
      >
        <TaskInstructions instructionsHtml={fileRequestTask?.instructions_html} />
        <DownloadFileLinks uploads={uploads} />
        {contentReviewCards}
      </Module>
    </Page>
  );
}
